// product-expansion-config.js - Product configuration registry
// Adding a new product = adding a new entry to ALL_PRODUCTS. No code changes needed.

(function(window) {
  'use strict';

  /**
   * Unified product configuration registry.
   *
   * Base products (type: "base") are the primary editing targets (e.g. the hoodie).
   * Expansion products (type: "expansion") derive designs from a base product via mappings.
   *
   * Every product defines:
   *   productId   - unique key
   *   type        - "base" | "expansion"
   *   views       - ordered array of view names (index = partIndex)
   *   boxes       - print area coordinates keyed by box name (700x700 display coords)
   *     Each box: { x, y, width, height, partIndex, clip, pad, autoMap }
   *     autoMap (optional) - how an AUTO-MAPPED design is sized and placed in
   *       this box. Without it the design is fitted to the whole box and
   *       centred (the historical behaviour). See getAutoMapPlacement.
   *         area   - {x,y,width,height} target rect to fit the design into,
   *                  when the editor box is much bigger than the real print
   *                  (sweatpants, shorts, headband). Defaults to the box.
   *                  On the four bottoms the editor box IS the whole garment -
   *                  the print area really is the whole front and the whole
   *                  back - so the rect is not a boundary, it is only where an
   *                  auto-mapped design LANDS before anyone drags it. Both
   *                  bottoms rects are the product's real print target
   *                  converted at that view's pxPerCm: front is the 13 cm
   *                  pantsLogo max pinned top-left to the documented anchor
   *                  (placement-config.json: 2.5 cm from the side seam, 3 cm
   *                  below the pocket seam), back is the 10 x 12 cm box from
   *                  print-sizing-config.json, centred across the garment at
   *                  the front rect's height. Leaving `area` off a bottoms box
   *                  is what made a back print auto-map to 65 cm across both
   *                  legs while the front sat correctly on one thigh.
   *         topCm  - put the design's TOP this many cm below the view's
   *                  collarY_px instead of centring it vertically.
   *       Since 2026-10-08 the FRONT bottoms rects' positions, the headband
   *       rect and the torso topCm values are where staff actually drag a
   *       design, measured over every hand-placed order in S3 ("take the
   *       manual placement"), not the press anchor converted to pixels. None
   *       of these slots prints from the editor: size comes from the sizing
   *       profile and position from placement-config.json's anchors, so this
   *       is the preview only. The sweatpants thigh rect also moved INSIDE its
   *       box: at x 202 it started 13 px left of the box, and the first touch
   *       clamped every logo to x 218.5. src/editor-boxes.json mirrors this
   *       (node scripts/gen-editor-boxes.mjs, then check-mirrors).
   *         align  - "top-left" pins the design to the area's corner.
   *   zoomScales  - per-box zoom scale overrides (boxName -> scale)
   *   uiConfig    - CSS class / selector hints for UI toggling
   *   defaultIncluded - false starts the product's Include box UNTICKED on a
   *     new order. Absent means true, which is how every product read before
   *     2026-09-02 and still how the ten mainline garments read. The three that
   *     opt out (pique, boxer, womens_briefs) are the ones most groups never
   *     buy, and a card that is ticked by default is a card somebody has to
   *     remember to untick on every single order. Only the DEFAULT moves: an
   *     edit-mode reopen still follows the saved manifest, which is read a few
   *     lines further down in product-creator-preview.js.
   *
   * Expansion products additionally define:
   *   handle, label, mappings (unchanged from before)
   */

  /**
   * Sleeve prints — which tops offer one, and on which arm.
   *
   * An optional badge on the arm, offered under "Advanced options" in the
   * garment's own section list the way the name wall's custom colour is. Never
   * on by default, never inherited from the hoodie: the customer uploads it per
   * product, per arm, or there is no sleeve print.
   *
   * THE ZIP HOODIE HAS ONE FREE ARM. Its left sleeve already carries the arm
   * name list, and two prints on one sleeve is two presses on one panel, which
   * the shop does not do. So the zip offers the RIGHT arm only; every other top
   * offers both.
   *
   * Mirrored in the backend as SLEEVE_PRINT_SIDES in src/lib/sleeve-print.js
   * and checked by scripts/check-mirrors.mjs. This decides which toggle is
   * drawn; that decides which slot exists in the print files. A product on one
   * list and not the other is a control that appears to work and ships nothing.
   *
   * The polo piqué is deliberately absent: its two sleeves are part of the
   * garment's own five placements and are always on, so it must not also grow
   * an Advanced toggle offering the same two boxes a second time.
   */
  var SLEEVE_PRINT_SIDES = {
    hoodie:     ["left_sleeve", "right_sleeve"],
    zip_hoodie: ["right_sleeve"],
    crewneck:   ["left_sleeve", "right_sleeve"],
    longsleeve: ["left_sleeve", "right_sleeve"],
    tshirt:     ["left_sleeve", "right_sleeve"]
  };

  // Every sleeve box name in play, for callers that only need the vocabulary.
  var SLEEVE_BOX_NAMES = ["left_sleeve", "right_sleeve"];

  var ALL_PRODUCTS = {
    // ===== BASE PRODUCT: Hoodie =====
    hoodie: {
      productId: "hoodie",
      type: "base",
      label: "Hoodie",
      pxPerCm: { front: { x: 5.85, y: 9.39 }, back: { x: 5.85, y: 7.97 },
                 left_sleeve: { x: 8.12, y: 8.12 }, right_sleeve: { x: 8.12, y: 8.12 } },
      edgeRefs: {
        front: { collarY_px: 118, centerX_px: 350, leftEdgeX_px: 185, rightEdgeX_px: 515, edgeLabel_x: "armpit seam" },
        back:  { collarY_px: 155, centerX_px: 350, leftEdgeX_px: 185, rightEdgeX_px: 515, edgeLabel_x: "armpit seam" },
        left_sleeve:  { collarY_px: 91, centerX_px: 345, ribbonY_px: 677 },
        right_sleeve: { collarY_px: 91, centerX_px: 345, ribbonY_px: 677 }
      },
      views: ["front", "back", "left_sleeve", "right_sleeve"],
      boxes: {
        front:       { x: 217, y: 196, width: 266, height: 294, partIndex: 0, clip: false, pad: 15 },
        back_top:    { x: 218, y: 240, width: 264, height: 90,  partIndex: 1, clip: true,  pad: 8  },
        back_middle: { x: 218, y: 330, width: 264, height: 217, partIndex: 1, clip: true,  pad: 10 },
        back_bottom: { x: 240, y: 546, width: 220, height: 34,  partIndex: 1, clip: true,  pad: 5  },
        hood_left:   { x: 224, y: 5,   width: 124, height: 150, partIndex: 1, clip: true,  pad: 5, snap: false },
        hood_right:  { x: 352, y: 5,   width: 124, height: 150, partIndex: 1, clip: true,  pad: 5, snap: false },
        left_sleeve:  { x: 275, y: 120, width: 140, height: 180, partIndex: 2, clip: false, pad: 5,
                        autoMap: { area: { x: 309, y: 148, width: 73, height: 65 } } },
        right_sleeve: { x: 275, y: 120, width: 140, height: 180, partIndex: 3, clip: false, pad: 5,
                        autoMap: { area: { x: 309, y: 148, width: 73, height: 65 } } }
      },
      placeholders: {
        front:       { type: "image", inputId: "input_front_logo" },
        back_top:    { type: "image", inputId: "input_back_top_logo" },
        back_middle: { type: "text",  inputId: "back_middle_input" },
        back_bottom: { type: "text",  inputId: "back_bottom_input" },
        hood_left:   { type: "image", inputId: "input_hood_left_logo" },
        hood_right:  { type: "image", inputId: "input_hood_right_logo" },
        left_sleeve:  { type: "image", inputId: "input_left_sleeve_logo" },
        right_sleeve: { type: "image", inputId: "input_right_sleeve_logo" }
      },
      zoomScales: {
        front: 1.4,
        hood_left: 1.6,
        hood_right: 1.6,
        back_top: 1.3,
        back_middle: 1.25,
        left_sleeve: 2.0,
        right_sleeve: 2.0
      },
      uiConfig: {
        modeButtonsClass: ".mode-buttons-hoodie",
        modeButtonsSelector: ".mode-buttons button",
        sectionListSelector: ".section-list:not(.section-list-expansion)",
        bodyClass: ""
      }
    },

    // ===== EXPANSION PRODUCT: Zip Hoodie =====
    zip_hoodie: {
      productId: "zip_hoodie",
      type: "expansion",
      pxPerCm: { front: { x: 5.81, y: 9.63 }, back: { x: 6.30, y: 8.27 }, arm: { x: 8.12, y: 8.12 },
                 right_sleeve: { x: 8.12, y: 8.12 } },
      edgeRefs: {
        front: { collarY_px: 115, centerX_px: 350, leftEdgeX_px: 185, rightEdgeX_px: 515, edgeLabel_x: "armpit seam", centerLineLabel: "zipper" },
        back:  { collarY_px: 149, centerX_px: 350, leftEdgeX_px: 180, rightEdgeX_px: 520, edgeLabel_x: "armpit seam" },
        arm:   { collarY_px: 91, centerX_px: 345 },
        // The zip's `arm` view IS the left arm — that is where the name list
        // goes. The badge gets its own sleeve view, drawn the same way: each
        // arm seen from its own outside puts the body on the same side, so
        // there is one drawing and one centre line for both.
        right_sleeve: { collarY_px: 91, centerX_px: 345, ribbonY_px: 677 }
      },
      handle: "hoodie-2",
      label: "Zip Hoodie",
      uiLabels: {
        front_chest: "Front chest",
        back_large: "Back",
        back_hood_left: "Left hood",
        back_hood_right: "Right hood",
        arm_teamwall: "Sleeve names",
        right_sleeve: "Right sleeve"
      },
      views: ["front", "back", "arm", "right_sleeve"],
      boxes: {
        // front_chest left edge = zipper (x=350) + 2cm (5.81 px/cm ≈ 12px), matching
        // the lambda print anchor; width capped so the right edge stays inside the
        // arm seam (rightEdgeX 515)
        front_chest:  { x: 362, y: 176, width: 150, height: 168, partIndex: 0, clip: false, pad: 10, snap: false },
        back_large:   { x: 211, y: 200, width: 278, height: 338, partIndex: 1, clip: false, pad: 12 },
        back_hood_left:  { x: 224, y: 5,   width: 124, height: 150, partIndex: 1, clip: false, pad: 5, snap: false },
        back_hood_right: { x: 352, y: 5,   width: 124, height: 150, partIndex: 1, clip: false, pad: 5, snap: false },
        back_bottom:  { x: 230, y: 540, width: 240, height: 36,  partIndex: 1, clip: false, pad: 5  },
        arm_teamwall: { x: 280, y: 180, width: 125, height: 399, partIndex: 2, clip: false, pad: 5  },
        right_sleeve: { x: 275, y: 120, width: 140, height: 180, partIndex: 3, clip: false, pad: 5,
                        autoMap: { area: { x: 309, y: 148, width: 73, height: 65 } } }
      },
      zoomScales: {
        front_chest:  1.6,
        back_large:   1.3,
        back_hood_left:  1.6,
        back_hood_right: 1.6,
        right_sleeve:    2.0
      },
      uiConfig: {
        bodyClass: "expansion-edit-mode"
      },
      mappings: [
        { from: "front",       fromPart: 0, toView: "back",  toBox: "back_large",   scaleMode: "fit" },
        { from: "back_top",    fromPart: 1, toView: "front", toBox: "front_chest",   scaleMode: "fit" },
        { from: "hood_left",   fromPart: 1, toView: "back",  toBox: "back_hood_left",  scaleMode: "preserve" },
        { from: "hood_right",  fromPart: 1, toView: "back",  toBox: "back_hood_right", scaleMode: "preserve" },
        { from: "back_middle", fromPart: 1, toView: "arm",   toBox: "arm_teamwall",  scaleMode: "reformat_teamwall" },
        { from: "back_bottom", fromPart: 1, toView: "back",  toBox: "back_bottom",   scaleMode: "placeholder_text", placeholderText: "ETTERNAVN" }
      ]
    },

    // ===== EXPANSION PRODUCT: Crewneck =====
    crewneck: {
      productId: "crewneck",
      type: "expansion",
      pxPerCm: { front: { x: 6.54, y: 11.10 }, back: { x: 6.54, y: 11.10 },
                 left_sleeve: { x: 8.12, y: 8.12 }, right_sleeve: { x: 8.12, y: 8.12 } },
      edgeRefs: {
        front: { collarY_px: 37, centerX_px: 350, leftEdgeX_px: 170, rightEdgeX_px: 530, edgeLabel_x: "armpit seam" },
        back:  { collarY_px: 37, centerX_px: 350, leftEdgeX_px: 170, rightEdgeX_px: 530, edgeLabel_x: "armpit seam" },
        left_sleeve:  { collarY_px: 91, centerX_px: 345, ribbonY_px: 677 },
        right_sleeve: { collarY_px: 91, centerX_px: 345, ribbonY_px: 677 }
      },
      handle: "crewneck",
      label: "Crewneck",
      uiLabels: {
        front_large: "Front",
        back_large: "Back",
        left_sleeve: "Left sleeve",
        right_sleeve: "Right sleeve"
      },
      views: ["front", "back", "left_sleeve", "right_sleeve"],
      boxes: {
        front_large: { x: 194, y: 121, width: 312, height: 478, partIndex: 0, clip: false, pad: 12, autoMap: { topCm: 12 } },
        back_large:  { x: 194, y: 121, width: 312, height: 478, partIndex: 1, clip: false, pad: 12, autoMap: { topCm: 11 } },
        left_sleeve:  { x: 275, y: 120, width: 140, height: 180, partIndex: 2, clip: false, pad: 5,
                        autoMap: { area: { x: 309, y: 148, width: 73, height: 65 } } },
        right_sleeve: { x: 275, y: 120, width: 140, height: 180, partIndex: 3, clip: false, pad: 5,
                        autoMap: { area: { x: 309, y: 148, width: 73, height: 65 } } }
      },
      zoomScales: { front_large: 1.3, back_large: 1.3, left_sleeve: 2.0, right_sleeve: 2.0 },
      uiConfig: { bodyClass: "expansion-edit-mode" },
      mappings: [
        { from: "front",    fromPart: 0, toView: "back",  toBox: "back_large",  scaleMode: "fit" },
        { from: "back_top", fromPart: 1, toView: "front", toBox: "front_large", scaleMode: "fit" }
      ]
    },

    // ===== EXPANSION PRODUCT: Polo piqué (5 placements, standalone) =====
    // Standalone single-product garment: front, back, both sleeves and an
    // inside-collar print. No hoodie inheritance (mappings empty) — every box is
    // uploaded directly in the single-product editor. Box names match the lambda
    // placement-config + print-resolver slot keys so designs/pique/<box>_logo.png
    // resolves end-to-end.
    pique: {
      productId: "pique",
      type: "expansion",
      defaultIncluded: false,
      pxPerCm: { front: { x: 8.22, y: 8.22 }, back: { x: 8.22, y: 8.22 }, left_sleeve: { x: 8.12, y: 8.12 }, right_sleeve: { x: 8.12, y: 8.12 }, inside_collar: { x: 8.22, y: 8.22 } },
      // Coords calibrated to the real white-polo mockups (600×750, contain-fit to
      // the 700² editor → rendered 560 wide, x-offset 70, scale ≈0.9333).
      edgeRefs: {
        front: { collarY_px: 173, centerX_px: 350, leftEdgeX_px: 150, rightEdgeX_px: 550, edgeLabel_x: "armpit seam" },
        back:  { collarY_px: 103, centerX_px: 350, leftEdgeX_px: 150, rightEdgeX_px: 550, edgeLabel_x: "armpit seam" },
        left_sleeve:  { collarY_px: 149, centerX_px: 322, ribbonY_px: 308 },
        right_sleeve: { collarY_px: 149, centerX_px: 322, ribbonY_px: 308 },
        inside_collar: { collarY_px: 60, centerX_px: 350 }
      },
      handle: "pique",
      label: "Polo piqué",
      uiLabels: {
        front_large: "Front",
        back_large: "Back",
        left_sleeve: "Left sleeve",
        right_sleeve: "Right sleeve",
        inside_collar: "Inside collar"
      },
      views: ["front", "back", "left_sleeve", "right_sleeve", "inside_collar"],
      boxes: {
        front_large:   { x: 366, y: 205, width: 99, height: 183, partIndex: 0, clip: false, pad: 8 },
        back_large:    { x: 229, y: 163, width: 243, height: 317, partIndex: 1, clip: false, pad: 12 },
        left_sleeve:   { x: 315, y: 163, width: 102, height: 131, partIndex: 2, clip: false, pad: 5 },
        right_sleeve:  { x: 283, y: 163, width: 102, height: 131, partIndex: 3, clip: false, pad: 5 },
        inside_collar: { x: 285, y: 120, width: 130, height: 70,  partIndex: 4, clip: false, pad: 5 }
      },
      zoomScales: { front_large: 1.3, back_large: 1.3, left_sleeve: 2.3, right_sleeve: 2.3, inside_collar: 2.0 },
      uiConfig: { bodyClass: "expansion-edit-mode" },
      mappings: []
    },

    // ===== EXPANSION PRODUCT: Longsleeve =====
    longsleeve: {
      productId: "longsleeve",
      type: "expansion",
      pxPerCm: 8.22,
      edgeRefs: {
        front: { collarY_px: 135, centerX_px: 350, leftEdgeX_px: 124, rightEdgeX_px: 576, edgeLabel_x: "armpit seam" },
        back:  { collarY_px: 135, centerX_px: 350, leftEdgeX_px: 124, rightEdgeX_px: 576, edgeLabel_x: "armpit seam" },
        left_sleeve:  { collarY_px: 91, centerX_px: 345, ribbonY_px: 677 },
        right_sleeve: { collarY_px: 91, centerX_px: 345, ribbonY_px: 677 }
      },
      handle: "longsleeve",
      label: "Longsleeve",
      uiLabels: {
        front_large: "Front",
        back_large: "Back",
        left_sleeve: "Left sleeve",
        right_sleeve: "Right sleeve"
      },
      views: ["front", "back", "left_sleeve", "right_sleeve"],
      boxes: {
        front_large: { x: 194, y: 161, width: 312, height: 408, partIndex: 0, clip: false, pad: 12, autoMap: { topCm: 8 } },
        back_large:  { x: 194, y: 161, width: 312, height: 408, partIndex: 1, clip: false, pad: 12, autoMap: { topCm: 6 } },
        left_sleeve:  { x: 275, y: 120, width: 140, height: 180, partIndex: 2, clip: false, pad: 5,
                        autoMap: { area: { x: 308, y: 148, width: 74, height: 66 } } },
        right_sleeve: { x: 275, y: 120, width: 140, height: 180, partIndex: 3, clip: false, pad: 5,
                        autoMap: { area: { x: 308, y: 148, width: 74, height: 66 } } }
      },
      zoomScales: { front_large: 1.3, back_large: 1.3, left_sleeve: 2.0, right_sleeve: 2.0 },
      uiConfig: { bodyClass: "expansion-edit-mode" },
      mappings: [
        { from: "front",    fromPart: 0, toView: "back",  toBox: "back_large",  scaleMode: "fit" },
        { from: "back_top", fromPart: 1, toView: "front", toBox: "front_large", scaleMode: "fit" }
      ]
    },

    // ===== EXPANSION PRODUCT: T-shirt =====
    tshirt: {
      productId: "tshirt",
      type: "expansion",
      pxPerCm: 8.22,
      edgeRefs: {
        front: { collarY_px: 135, centerX_px: 350, leftEdgeX_px: 124, rightEdgeX_px: 576, edgeLabel_x: "armpit seam" },
        back:  { collarY_px: 140, centerX_px: 350, leftEdgeX_px: 124, rightEdgeX_px: 576, edgeLabel_x: "armpit seam" },
        // SHORT sleeve, so the view is framed like the polo piqué's rather
        // than like the long-sleeve side views: the hem sits high and there is
        // one panel, not a whole arm. Calibrated to the piqué's sleeve views
        // pending the t-shirt's own mockups.
        left_sleeve:  { collarY_px: 149, centerX_px: 366, ribbonY_px: 308 },
        right_sleeve: { collarY_px: 149, centerX_px: 366, ribbonY_px: 308 }
      },
      handle: "t-shirt",
      label: "T-skjorte",
      uiLabels: {
        front_large: "Front",
        back_large: "Back",
        left_sleeve: "Left sleeve",
        right_sleeve: "Right sleeve"
      },
      views: ["front", "back", "left_sleeve", "right_sleeve"],
      boxes: {
        front_large: { x: 194, y: 191, width: 312, height: 408, partIndex: 0, clip: false, pad: 12, autoMap: { topCm: 10 } },
        back_large:  { x: 194, y: 191, width: 312, height: 408, partIndex: 1, clip: false, pad: 12, autoMap: { topCm: 7 } },
        left_sleeve:  { x: 315, y: 163, width: 102, height: 131, partIndex: 2, clip: false, pad: 5,
                        autoMap: { area: { x: 329, y: 190, width: 74, height: 66 } } },
        right_sleeve: { x: 315, y: 163, width: 102, height: 131, partIndex: 3, clip: false, pad: 5,
                        autoMap: { area: { x: 329, y: 190, width: 74, height: 66 } } }
      },
      zoomScales: { front_large: 1.3, back_large: 1.3, left_sleeve: 2.3, right_sleeve: 2.3 },
      uiConfig: { bodyClass: "expansion-edit-mode" },
      mappings: [
        { from: "back_top", fromPart: 1, toView: "front", toBox: "front_large", scaleMode: "fit" }
      ]
    },

    // ===== EXPANSION PRODUCT: Singlet/Tanktop =====
    singlet: {
      productId: "singlet",
      type: "expansion",
      pxPerCm: 8.22,
      edgeRefs: {
        front: { collarY_px: 170, centerX_px: 350, leftEdgeX_px: 186, rightEdgeX_px: 514, edgeLabel_x: "armpit seam" },
        back:  { collarY_px: 170, centerX_px: 350, leftEdgeX_px: 186, rightEdgeX_px: 514, edgeLabel_x: "armpit seam" }
      },
      handle: "singlet",
      label: "Tanktop",
      uiLabels: {
        front_large: "Front",
        back_large: "Back"
      },
      views: ["front", "back"],
      boxes: {
        front_large: { x: 218, y: 186, width: 264, height: 408, partIndex: 0, clip: false, pad: 12, autoMap: { topCm: 8 } },
        back_large:  { x: 218, y: 186, width: 264, height: 408, partIndex: 1, clip: false, pad: 12, autoMap: { topCm: 8 } }
      },
      zoomScales: { front_large: 1.3, back_large: 1.3 },
      uiConfig: { bodyClass: "expansion-edit-mode" },
      mappings: [
        { from: "back_top", fromPart: 1, toView: "front", toBox: "front_large", scaleMode: "fit" }
      ]
    },

    // ===== EXPANSION PRODUCT: Unisex Sweatpants =====
    unisex_sweatpants: {
      productId: "unisex_sweatpants",
      type: "expansion",
      pxPerCm: { front: { x: 5.54, y: 6.43 }, back: { x: 4.45, y: 6.41 } },
      edgeRefs: {
        front: { collarY_px: 4, centerX_px: 350 },
        back:  { collarY_px: 4, centerX_px: 350 }
      },
      handle: "unisex-sweatpants",
      label: "Joggebukse",
      uiLabels: {
        front_logo: "Front",
        back_logo: "Back"
      },
      views: ["front", "back"],
      boxes: {
        front_logo: { x: 215, y: 20, width: 270, height: 660, partIndex: 0, clip: false, pad: 8,
                      autoMap: { area: { x: 218.5, y: 166, width: 72, height: 84 }, align: "top-left" } },
        back_logo:  { x: 205, y: 20, width: 290, height: 660, partIndex: 1, clip: false, pad: 8,
                      autoMap: { area: { x: 328, y: 194, width: 45, height: 77 } } }
      },
      zoomScales: { front_logo: 1.0, back_logo: 1.0 },
      uiConfig: { bodyClass: "expansion-edit-mode" },
      mappings: [
        { from: "back_top", fromPart: 1, toView: "front", toBox: "front_logo", scaleMode: "fit" }
      ]
    },

    // ===== EXPANSION PRODUCT: Women's Sweatpants =====
    womens_sweatpants: {
      productId: "womens_sweatpants",
      type: "expansion",
      pxPerCm: { front: { x: 4.76, y: 6.60 }, back: { x: 5.10, y: 6.54 } },
      edgeRefs: {
        front: { collarY_px: 4, centerX_px: 350 },
        back:  { collarY_px: 4, centerX_px: 350 }
      },
      handle: "womens-sweatpants",
      label: "Joggebukse Dame",
      uiLabels: {
        front_logo: "Front",
        back_logo: "Back"
      },
      views: ["front", "back"],
      boxes: {
        front_logo: { x: 170, y: 15, width: 360, height: 670, partIndex: 0, clip: false, pad: 8,
                      autoMap: { area: { x: 235, y: 140, width: 62, height: 86 }, align: "top-left" } },
        back_logo:  { x: 200, y: 15, width: 300, height: 670, partIndex: 1, clip: false, pad: 8,
                      autoMap: { area: { x: 325, y: 178, width: 51, height: 78 } } }
      },
      zoomScales: { front_logo: 1.0, back_logo: 1.0 },
      uiConfig: { bodyClass: "expansion-edit-mode" },
      mappings: [
        { from: "back_top", fromPart: 1, toView: "front", toBox: "front_logo", scaleMode: "fit" }
      ]
    },

    // ===== EXPANSION PRODUCT: Unisex Shorts =====
    unisex_shorts: {
      productId: "unisex_shorts",
      type: "expansion",
      pxPerCm: 8.22,
      edgeRefs: {
        front: { collarY_px: 10, centerX_px: 350 },
        back:  { collarY_px: 10, centerX_px: 350 }
      },
      handle: "mens-shorts",
      label: "Shorts",
      uiLabels: {
        front_logo: "Front",
        back_logo: "Back"
      },
      views: ["front", "back"],
      boxes: {
        front_logo: { x: 15, y: 15, width: 670, height: 670, partIndex: 0, clip: false, pad: 8,
                      autoMap: { area: { x: 97, y: 285, width: 107, height: 107 }, align: "top-left" } },
        back_logo:  { x: 40, y: 15, width: 620, height: 670, partIndex: 1, clip: false, pad: 8,
                      autoMap: { area: { x: 309, y: 295, width: 82, height: 99 } } }
      },
      zoomScales: { front_logo: 1.0, back_logo: 1.0 },
      uiConfig: { bodyClass: "expansion-edit-mode" },
      mappings: [
        { from: "back_top", fromPart: 1, toView: "front", toBox: "front_logo", scaleMode: "fit" }
      ]
    },

    // ===== EXPANSION PRODUCT: Women's Shorts =====
    womens_shorts: {
      productId: "womens_shorts",
      type: "expansion",
      pxPerCm: 8.22,
      edgeRefs: {
        front: { collarY_px: 10, centerX_px: 350 },
        back:  { collarY_px: 10, centerX_px: 350 }
      },
      handle: "womens-shorts",
      label: "Shorts Dame",
      uiLabels: {
        front_logo: "Front",
        back_logo: "Back"
      },
      views: ["front", "back"],
      boxes: {
        front_logo: { x: 40, y: 15, width: 620, height: 670, partIndex: 0, clip: false, pad: 8,
                      autoMap: { area: { x: 133, y: 295, width: 107, height: 107 }, align: "top-left" } },
        back_logo:  { x: 40, y: 15, width: 620, height: 670, partIndex: 1, clip: false, pad: 8,
                      autoMap: { area: { x: 309, y: 275, width: 82, height: 99 } } }
      },
      zoomScales: { front_logo: 1.0, back_logo: 1.0 },
      uiConfig: { bodyClass: "expansion-edit-mode" },
      mappings: [
        { from: "back_top", fromPart: 1, toView: "front", toBox: "front_logo", scaleMode: "fit" }
      ]
    },

    // ===== EXPANSION PRODUCT: Headband =====
    headband: {
      productId: "headband",
      type: "expansion",
      pxPerCm: 42.86,
      edgeRefs: {
        front: { collarY_px: 340, centerX_px: 350 }
      },
      handle: "hodeband",
      label: "Hodebånd",
      uiLabels: {
        front_logo: "Logo"
      },
      views: ["front"],
      boxes: {
        front_logo: { x: 45, y: 405, width: 600, height: 110, partIndex: 0, clip: false, pad: 8,
                      autoMap: { area: { x: 45, y: 410, width: 600, height: 90 } } }
      },
      zoomScales: { front_logo: 1.5 },
      uiConfig: { bodyClass: "expansion-edit-mode" },
      mappings: [
        { from: "back_top", fromPart: 1, toView: "front", toBox: "front_logo", scaleMode: "fit" }
      ]
    },

    // ===== EXPANSION PRODUCT: Boxer (men's) =====
    // Front only, one logo, ticked off by default. The mockup is the customer's
    // own flat art redrawn at OUTPUT_SIZE (1400²), so the garment's own pixels
    // fix pxPerCm: it is drawn 570 px wide in this 700-space against a 38 cm
    // flat hem, i.e. 15 px/cm. Every coordinate below was measured off that
    // template, not guessed — the waistband top sits at y 105 and the yoke seam
    // clears y 216 at the print's leftmost column.
    //
    // The print goes where the shorts print goes (Alexander, 2026-09-02, second
    // pass): low on the front, on the WEARER's right, which is the left of a
    // front-view mockup — the same side unisex_sweatpants and both shorts
    // anchor to, and the reason all of them use `originX: leftEdgeX_px`. First
    // pass had it high on the viewer's right, i.e. the wearer's left.
    //
    // 22 cm below the waistband and 3 cm in from the left side seam, measured on
    // this template: the leg panel is clear from the yoke seam (y 216 at its
    // lowest) to the hem band (y 590), and the print at y 435-555 clears the hem
    // by 2.3 cm. 3 cm rather than the shorts' 2.5 because 2.5 cm lands on a half
    // pixel at 15 px/cm, and a boxer leg is narrower than a shorts leg so the
    // slightly deeper inset reads the same.
    //
    // `align: "top-left"` pins the auto-mapped design to the area corner, which
    // is what makes the seam distance hold whatever shape the artwork is —
    // exactly as the four bottoms do it.
    boxer: {
      productId: "boxer",
      type: "expansion",
      defaultIncluded: false,
      pxPerCm: 15,
      edgeRefs: {
        front: { collarY_px: 105, centerX_px: 350, leftEdgeX_px: 72, rightEdgeX_px: 590, edgeLabel: "pas" }
      },
      handle: "boxer",
      label: "Boxer",
      uiLabels: {
        front_logo: "Front"
      },
      views: ["front"],
      boxes: {
        front_logo: { x: 65, y: 100, width: 570, height: 525, partIndex: 0, clip: false, pad: 8,
                      autoMap: { area: { x: 117, y: 435, width: 150, height: 120 }, align: "top-left" } }
      },
      zoomScales: { front_logo: 1.0 },
      uiConfig: { bodyClass: "expansion-edit-mode" },
      mappings: [
        { from: "back_top", fromPart: 1, toView: "front", toBox: "front_logo", scaleMode: "fit" }
      ]
    },

    // ===== EXPANSION PRODUCT: Truse (women's briefs) =====
    // Front only, one logo, ticked off by default. Same derivation as the boxer
    // above: drawn 650 px wide in this 700-space against a 26 cm flat waistband,
    // i.e. 25 px/cm, waistband top at y 176 and the garment ending at y 526.
    //
    // Redrawn 2026-09-02 (second pass). It was 20 px/cm, which left a third of
    // the canvas empty above and below a garment that is 1.85 times wider than
    // it is tall — "truse should be trimmed the top and bottom white space".
    // Cropping the source cannot fix that, because both this canvas and the
    // guide's contain-fit the template back into a square and re-pad it; the
    // garment has to be DRAWN bigger, so it is, and every coordinate here moved
    // with it.
    //
    // The print is centred horizontally and pinned 3 cm below the waistband
    // ("larger and moved more upwards"). `topCm` is what pins it: no `align`, so
    // getAutoMapPlacement centres the design in the area, and topCm then
    // overrides the vertical. The area is the print's real bounding box —
    // briefsLogo's 9 cm max by its 6 cm ink ceiling — rather than the square the
    // four bottoms use, because those have no ceiling to size the second side
    // from and these do. Squared off, a 1:1 upload would auto-map 9 cm tall and
    // hang over the leg openings, where the garment is only 6 cm across.
    womens_briefs: {
      productId: "womens_briefs",
      type: "expansion",
      defaultIncluded: false,
      pxPerCm: 25,
      edgeRefs: {
        front: { collarY_px: 176, centerX_px: 350, leftEdgeX_px: 25, rightEdgeX_px: 675, edgeLabel: "pas" }
      },
      handle: "truse",
      label: "Truse",
      uiLabels: {
        front_logo: "Front"
      },
      views: ["front"],
      boxes: {
        front_logo: { x: 25, y: 174, width: 650, height: 352, partIndex: 0, clip: false, pad: 8,
                      autoMap: { area: { x: 237.5, y: 251, width: 225, height: 150 }, topCm: 3 } }
      },
      zoomScales: { front_logo: 1.0 },
      uiConfig: { bodyClass: "expansion-edit-mode" },
      mappings: [
        { from: "back_top", fromPart: 1, toView: "front", toBox: "front_logo", scaleMode: "fit" }
      ]
    }
  };

  // Current active product ID (defaults to hoodie)
  var activeProductId = "hoodie";

  // Which view every mapping target renders in, harvested from the configs
  // themselves so a new product cannot drift from this table. Box names are
  // global vocabulary (back_large is the back on every garment that has one),
  // and the harvest asserts that: a box that resolved to two different views
  // anywhere would be a naming bug, not something to paper over.
  var BOX_VIEW = {};
  for (var _pid in ALL_PRODUCTS) {
    if (!ALL_PRODUCTS.hasOwnProperty(_pid)) continue;
    (ALL_PRODUCTS[_pid].mappings || []).forEach(function (m) {
      if (!m || !m.toBox || !m.toView) return;
      if (BOX_VIEW[m.toBox] && BOX_VIEW[m.toBox] !== m.toView) {
        console.warn("[ProductConfig] box " + m.toBox + " renders in both " +
          BOX_VIEW[m.toBox] + " and " + m.toView);
      }
      BOX_VIEW[m.toBox] = m.toView;
    });
  }

  // The two sleeve boxes are never mapped — a sleeve badge is uploaded, never
  // inherited from the hoodie — so the sweep above never reaches them and
  // viewForBox would fall through to its prefix rule, which asks whether "left"
  // is a view and lands on `views[0]` when it is not. Each sleeve box draws
  // into the view of the same name, on every product that has one; the polo
  // piqué's do too, which is why this is stated once here rather than per
  // product.
  BOX_VIEW.left_sleeve = "left_sleeve";
  BOX_VIEW.right_sleeve = "right_sleeve";

  // ===== Unified ProductConfig API =====
  window.ProductConfig = {
    /** Get product config by ID */
    getProduct: function(id) {
      return ALL_PRODUCTS[id] || null;
    },

    /**
     * The view `boxName` draws into on product `id`.
     *
     * The product's own mapping is authoritative. A box the product declares
     * but does not map (see unmappedBoxes) has no mapping to ask, so it falls
     * back to the shared vocabulary, then to the box name's own prefix.
     */
    viewForBox: function(id, boxName) {
      var product = ALL_PRODUCTS[id];
      if (!product || !boxName) return null;
      var mappings = product.mappings || [];
      for (var i = 0; i < mappings.length; i++) {
        if (mappings[i].toBox === boxName) return mappings[i].toView;
      }
      var views = product.views || [];
      var shared = BOX_VIEW[boxName];
      if (shared && views.indexOf(shared) !== -1) return shared;
      var prefix = String(boxName).split("_")[0];
      if (views.indexOf(prefix) !== -1) return prefix;
      return views[0] || null;
    },

    /**
     * Boxes the product declares but no mapping fills.
     *
     * These are real, offered slots: they are in `boxes` and usually in
     * `uiLabels`, so the expansion editor renders an upload target for them and
     * the customer can put artwork there. What they are NOT is auto-mapped from
     * the hoodie — the t-shirt and the tanktop declare `back_large` with no
     * mapping precisely so their backs stay empty unless someone asks for one.
     *
     * Everything downstream used to iterate `mappings` alone, so such an upload
     * was accepted, stored and uploaded to S3, and then rendered by nothing and
     * collected into no print file. On the Hefners order (read 2026-08-14) that
     * silently dropped the t-shirt's back print — the artwork was sitting in
     * expansion-variant-logos/tshirt/Hvit/back_large.png while designs/tshirt/
     * held a front file and nothing else. See effectiveMappings.
     */
    unmappedBoxes: function(id) {
      var product = ALL_PRODUCTS[id];
      if (!product || !product.boxes) return [];
      var mapped = {};
      (product.mappings || []).forEach(function (m) { if (m && m.toBox) mapped[m.toBox] = true; });
      var out = [];
      for (var name in product.boxes) {
        if (product.boxes.hasOwnProperty(name) && !mapped[name]) out.push(name);
      }
      return out;
    },

    /**
     * This product's mappings, plus a synthetic one for every box in
     * `filledBoxes` the config does not map.
     *
     * A synthetic mapping carries `from: null`: there is no hoodie slot to
     * inherit from, the customer's own upload IS the source. Every consumer
     * already skips a mapping whose `from` resolves to nothing, so a synthetic
     * one contributes only where explicit artwork exists — it can add a print
     * the customer asked for and can never invent one they did not.
     *
     * Pass the boxes that actually hold artwork (VariantLogoUtil.uploadedBoxes);
     * passing nothing returns the configured mappings unchanged.
     */
    effectiveMappings: function(id, filledBoxes) {
      var product = ALL_PRODUCTS[id];
      if (!product) return [];
      var mappings = (product.mappings || []).slice();
      if (!filledBoxes || !filledBoxes.length) return mappings;
      var mapped = {};
      mappings.forEach(function (m) { if (m && m.toBox) mapped[m.toBox] = true; });
      var self = this;
      filledBoxes.forEach(function (boxName) {
        if (mapped[boxName] || !product.boxes[boxName]) return;
        mapped[boxName] = true;
        mappings.push({
          from: null,
          fromPart: 0,
          toView: self.viewForBox(id, boxName),
          toBox: boxName,
          scaleMode: "fit",
          synthetic: true
        });
      });
      return mappings;
    },

    /** Get the currently active product config */
    getActiveProduct: function() {
      return ALL_PRODUCTS[activeProductId] || ALL_PRODUCTS.hoodie;
    },

    /** Get the currently active product ID */
    getActiveProductId: function() {
      return activeProductId;
    },

    /** Set the active product ID */
    setActiveProductId: function(id) {
      if (ALL_PRODUCTS[id]) {
        activeProductId = id;
        // Keep backward-compat global in sync
        window.CUSTOMIZER_MODE = id;
      }
    },

    /** Get all boxes for a given partIndex on a product */
    getBoxesForPartIndex: function(id, partIndex) {
      var product = ALL_PRODUCTS[id];
      if (!product) return {};
      var result = {};
      var boxes = product.boxes;
      for (var name in boxes) {
        if (boxes.hasOwnProperty(name) && boxes[name].partIndex === partIndex) {
          result[name] = boxes[name];
        }
      }
      return result;
    },

    /** Get a single box by name on a product */
    getBoxByName: function(id, boxName) {
      var product = ALL_PRODUCTS[id];
      if (!product || !boxName) return null;
      return product.boxes[boxName] || null;
    },

    /**
     * Size and position for an AUTO-MAPPED design of natural size imgW × imgH
     * in `boxName` on product `id`. Returns { scale, centerX, centerY } in
     * 700-space, or null when the box or the dimensions are unusable.
     *
     * The default — fit the whole editor box, centred — is what every box got
     * before, and it is still what a box with no `autoMap` rule gets. It is
     * wrong wherever the editor box is not the print area:
     *
     *   autoMap.area   The real print rect. Sweatpants, shorts and the headband
     *                  use the whole garment as their editor box, so a mapped
     *                  logo was blown up across both legs / off the band. The
     *                  area is the ~13 cm print those garments actually take,
     *                  read off the mockup with the same seam references
     *                  placement-config.json anchors to.
     *   autoMap.topCm  Torso garments: put the design's TOP this many cm below
     *                  the view's collarY_px instead of centring it in a box
     *                  much taller than the print, which sank short designs to
     *                  belly height. Clamped into the area.
     *   autoMap.align  "top-left" pins the design into the area's corner, which
     *                  is where a thigh logo goes.
     */
    getAutoMapPlacement: function(id, boxName, imgW, imgH) {
      var product = ALL_PRODUCTS[id];
      var box = product && product.boxes && product.boxes[boxName];
      if (!box || !(imgW > 0) || !(imgH > 0)) return null;

      var rule = box.autoMap || null;
      var area = (rule && rule.area) || box;

      var scale = Math.min(area.width / imgW, area.height / imgH);
      var w = imgW * scale;
      var h = imgH * scale;

      var topLeft = rule && rule.align === "top-left";
      var left = topLeft ? area.x : area.x + (area.width - w) / 2;
      var top = topLeft ? area.y : area.y + (area.height - h) / 2;

      if (rule && rule.topCm != null) {
        var view = product.views && product.views[box.partIndex];
        var ref = product.edgeRefs && product.edgeRefs[view];
        var pxPerCm = product.pxPerCm;
        var ppcY = typeof pxPerCm === "number"
          ? pxPerCm
          : (pxPerCm && pxPerCm[view] && pxPerCm[view].y);
        if (ref && ref.collarY_px != null && ppcY) {
          top = ref.collarY_px + rule.topCm * ppcY;
          var maxTop = area.y + area.height - h;
          if (top > maxTop) top = maxTop;
          if (top < area.y) top = area.y;
        }
      }

      return { scale: scale, centerX: left + w / 2, centerY: top + h / 2 };
    },

    /** Get zoom configs (boxName -> {scale, translateX, translateY}) for a product */
    getZoomConfigs: function(id, getZoomConfigFn) {
      var product = ALL_PRODUCTS[id];
      if (!product || !product.zoomScales || !getZoomConfigFn) return {};
      var result = {};
      for (var boxName in product.zoomScales) {
        if (product.zoomScales.hasOwnProperty(boxName)) {
          var box = product.boxes[boxName];
          result[boxName] = getZoomConfigFn(box, product.zoomScales[boxName]);
        }
      }
      result.reset = getZoomConfigFn(null, 1);
      return result;
    },

    /** Check if active product is a base product */
    isBaseProduct: function() {
      var product = ALL_PRODUCTS[activeProductId];
      return product ? product.type === "base" : true;
    },

    /** Check if active product is an expansion product */
    isExpansionProduct: function() {
      var product = ALL_PRODUCTS[activeProductId];
      return product ? product.type === "expansion" : false;
    },

    /** Get all product IDs */
    getProductIds: function() {
      return Object.keys(ALL_PRODUCTS);
    },

    /** The sleeve-print sides table itself (see SLEEVE_PRINT_SIDES). */
    SLEEVE_PRINT_SIDES: SLEEVE_PRINT_SIDES,
    SLEEVE_BOX_NAMES: SLEEVE_BOX_NAMES,

    /**
     * The sleeve boxes this product may offer. productId may be versioned
     * ("hoodie__v1") — a version has its base product's geometry by definition.
     * Returns a fresh array, [] for a product with no sleeve print.
     */
    sleeveBoxesFor: function(productId) {
      var base = String(productId || "").replace(/__v\d+$/, "");
      return (SLEEVE_PRINT_SIDES[base] || []).slice();
    },

    /** Is this box name one of the two sleeve boxes? */
    isSleeveBox: function(boxName) {
      return SLEEVE_BOX_NAMES.indexOf(boxName) !== -1;
    },

    /** Get all expansion product IDs */
    getExpansionProductIds: function() {
      return Object.keys(ALL_PRODUCTS).filter(function(id) {
        return ALL_PRODUCTS[id].type === "expansion";
      });
    }
  };

  // ===== Legacy ExpansionConfig API (backward compat) =====
  // Filters to expansion products only, keeps existing call sites working.
  var EXPANSION_PRODUCTS = {};
  for (var id in ALL_PRODUCTS) {
    if (ALL_PRODUCTS.hasOwnProperty(id) && ALL_PRODUCTS[id].type === "expansion") {
      EXPANSION_PRODUCTS[id] = ALL_PRODUCTS[id];
    }
  }

  window.ExpansionConfig = {
    products: EXPANSION_PRODUCTS,

    getProduct: function(productId) {
      return EXPANSION_PRODUCTS[productId] || null;
    },

    getProductIds: function() {
      return Object.keys(EXPANSION_PRODUCTS);
    },

    getBoxesForPartIndex: function(productId, partIndex) {
      var product = EXPANSION_PRODUCTS[productId];
      if (!product) return {};
      var viewName = product.views[partIndex];
      if (!viewName) return {};
      var result = {};
      for (var i = 0; i < product.mappings.length; i++) {
        var m = product.mappings[i];
        if (m.toView === viewName && product.boxes[m.toBox]) {
          result[m.toBox] = product.boxes[m.toBox];
        }
      }
      return result;
    }
  };

})(window);
